import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  validateManagerInvite,
  acceptManagerInvite,
  registerManager,
  loginManager,
  ManagerInviteValidation,
} from '../../api/leagues';
import '../../styles/global.css';

const AcceptManagerInvite: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [invite, setInvite] = useState<ManagerInviteValidation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('register');

  // Registration form
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [registering, setRegistering] = useState(false);

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      if (!token) return;
      try {
        const data = await validateManagerInvite(token);
        setInvite(data);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Invalid or expired invitation');
      } finally {
        setLoading(false);
      }
    };

    // Check if already logged in
    const managerToken = localStorage.getItem('manager_token');
    if (managerToken) {
      setIsLoggedIn(true);
    }

    fetchInvite();
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setError('');
    setAccepting(true);

    const managerToken = localStorage.getItem('manager_token');
    localStorage.setItem('token', managerToken || '');

    try {
      const result = await acceptManagerInvite(token);
      navigate(`/manager/leagues/${result.league_id}`, {
        state: { success: `You are now a manager of ${result.league_name}!` }
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      localStorage.removeItem('token');
      setAccepting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setRegistering(true);

    try {
      const result = await registerManager(regEmail, regPassword, regName);
      localStorage.setItem('manager_token', result.access_token);
      setIsLoggedIn(true);

      // Auto-accept after registration
      localStorage.setItem('token', result.access_token);
      try {
        const acceptResult = await acceptManagerInvite(token!);
        navigate(`/manager/leagues/${acceptResult.league_id}`, {
          state: { success: `Welcome! You are now a manager of ${acceptResult.league_name}!` }
        });
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Registration succeeded but failed to accept invite');
      } finally {
        localStorage.removeItem('token');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoggingIn(true);

    try {
      const result = await loginManager(loginEmail, loginPassword);
      localStorage.setItem('manager_token', result.access_token);
      setIsLoggedIn(true);

      // Auto-accept after login
      localStorage.setItem('token', result.access_token);
      try {
        const acceptResult = await acceptManagerInvite(token!);
        navigate(`/manager/leagues/${acceptResult.league_id}`, {
          state: { success: `You are now a manager of ${acceptResult.league_name}!` }
        });
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Login succeeded but failed to accept invite');
      } finally {
        localStorage.removeItem('token');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoggingIn(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading invitation...</div>;
  }

  if (!invite || invite.status !== 'pending') {
    return (
      <div className="page">
        <div className="container" style={{ maxWidth: '500px', marginTop: '60px' }}>
          <div className="card">
            <h2>Invalid Invitation</h2>
            <p className="error">
              {error || 'This invitation is no longer valid. It may have expired or already been used.'}
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/manager')}>
              Go to Manager Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container" style={{ maxWidth: '500px', marginTop: '60px' }}>
        <div className="card">
          <h2>Manager Invitation</h2>
          <p style={{ marginBottom: '20px' }}>
            <strong>{invite.inviter_name}</strong> has invited you to co-manage{' '}
            <strong>{invite.league_name}</strong>.
          </p>

          {error && <div className="error" style={{ marginBottom: '15px' }}>{error}</div>}

          {isLoggedIn ? (
            <div>
              <p style={{ marginBottom: '15px' }}>
                You are logged in. Click below to accept the invitation.
              </p>
              <button
                className="btn btn-primary"
                onClick={handleAccept}
                disabled={accepting}
                style={{ width: '100%' }}
              >
                {accepting ? 'Accepting...' : `Accept Invite to ${invite.league_name}`}
              </button>
            </div>
          ) : (
            <div>
              <p style={{ marginBottom: '15px', color: '#666' }}>
                Register or log in to accept this invitation.
              </p>

              {/* Tab buttons */}
              <div style={{ display: 'flex', gap: '0', marginBottom: '20px' }}>
                <button
                  onClick={() => setActiveTab('register')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderBottom: activeTab === 'register' ? '2px solid #4CAF50' : '1px solid #ddd',
                    backgroundColor: activeTab === 'register' ? '#f9f9f9' : '#fff',
                    fontWeight: activeTab === 'register' ? 600 : 400,
                    cursor: 'pointer',
                  }}
                >
                  Register
                </button>
                <button
                  onClick={() => setActiveTab('login')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderBottom: activeTab === 'login' ? '2px solid #4CAF50' : '1px solid #ddd',
                    backgroundColor: activeTab === 'login' ? '#f9f9f9' : '#fff',
                    fontWeight: activeTab === 'login' ? 600 : 400,
                    cursor: 'pointer',
                  }}
                >
                  Login
                </button>
              </div>

              {activeTab === 'register' ? (
                <form onSubmit={handleRegister} className="form">
                  <div className="form-group">
                    <label htmlFor="regName">Name</label>
                    <input
                      type="text"
                      id="regName"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      required
                      placeholder="Your name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="regEmail">Email</label>
                    <input
                      type="email"
                      id="regEmail"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="regPassword">Password</label>
                    <input
                      type="password"
                      id="regPassword"
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="At least 6 characters"
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={registering}
                    style={{ width: '100%' }}>
                    {registering ? 'Registering...' : 'Register & Accept Invite'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleLogin} className="form">
                  <div className="form-group">
                    <label htmlFor="loginEmail">Email</label>
                    <input
                      type="email"
                      id="loginEmail"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="loginPassword">Password</label>
                    <input
                      type="password"
                      id="loginPassword"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      placeholder="Your password"
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={loggingIn}
                    style={{ width: '100%' }}>
                    {loggingIn ? 'Logging in...' : 'Login & Accept Invite'}
                  </button>
                </form>
              )}
            </div>
          )}

          <p style={{ fontSize: '12px', color: '#999', marginTop: '20px' }}>
            This invitation expires on {new Date(invite.expires_at).toLocaleDateString()}.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcceptManagerInvite;
